<template>
    <v-container fluid class="pa-0 checklist-editor">
        <!-- Warning for Always activation -->
        <v-alert v-if="hasAlwaysActivation" type="info" dense class="mb-4">
            <strong>Always Active:</strong>
            This template will be active for all jobs. Other activation rules will be ignored.
        </v-alert>

        <!-- Warning for Has Any Contract activation -->
        <v-alert v-if="hasAnyContractActivation" type="warning" dense class="mb-4">
            <strong>Contract Scope:</strong>
            This activation covers all contract-related rules. Individual contract and contract type activations will be
            ignored.
        </v-alert>

        <v-data-table :headers="activationHeaders" :items="template.CheckListActivations || []" hide-default-footer>

            <template v-slot:[`item.ActivationType`]="{ item }">
                <v-select v-model="item.ActivationType" :items="availableActivationTypes(item)" item-text="text"
                    item-value="value" dense @change="onActivationTypeChange(item)">
                    <template v-slot:selection="{ item }">
                        <v-icon left small>{{ item.icon }}</v-icon>
                        {{ item.text }}
                    </template>
                    <template v-slot:item="{ item }">
                        <v-icon left small>{{ item.icon }}</v-icon>
                        {{ item.text }}
                    </template>
                </v-select>
            </template>

            <template v-slot:[`item.ActivationKey`]="{ item }">
                <!-- Dynamic dropdown based on activation type -->
                <div v-if="item.ActivationType === activationTypes.Always.value">
                    <span class="text-caption grey--text">Always active</span>
                </div>
                <div
                    v-else-if="activationTypeMap[item.ActivationType] && activationTypeMap[item.ActivationType].pickerComponent">
                    <component :is="activationTypeMap[item.ActivationType].pickerComponent" v-model="item.ActivationKey"
                        dense clearable externaledit @input="updateActivation(item)">
                    </component>
                </div>
                <div v-else-if="item.ActivationType === activationTypes.AnyContract.value">
                    <v-switch v-model="item.ActivationKey" :true-value="true" :false-value="false" dense
                        @change="updateActivation(item)">
                        <template v-slot:label>
                            <span class="text-caption">Has any contract</span>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on" small class="ml-1">mdi-information</v-icon>
                                </template>
                                <span>If false, this will only apply to jobs that have no contracts</span>
                            </v-tooltip>
                        </template>
                    </v-switch>
                </div>
                <div v-else-if="item.ActivationType === activationTypes.ContractType.value">
                    <v-select v-model="item.ActivationKey" :items="contractTypeList" item-text="Name" item-value="Id"
                        dense clearable @change="updateActivation(item)">
                    </v-select>
                </div>
                <div v-else-if="item.ActivationType === activationTypes.JobPriority.value">
                    <v-select v-model="item.ActivationKey" :items="jobPriorityList" item-text="Name" item-value="Id"
                        dense clearable @change="updateActivation(item)">
                    </v-select>
                </div>
                <div v-else-if="item.ActivationType === activationTypes.JobType.value">
                    <v-select v-model="item.ActivationKey" :items="jobTypeList" item-text="Name" item-value="Id" dense
                        clearable @change="updateActivation(item)">
                    </v-select>
                </div>
                <div v-else>
                    <v-text-field v-model="item.ActivationKey" dense @change="updateActivation(item)"></v-text-field>
                </div>
            </template>

            <template v-slot:[`item.Optional`]="{ item }">
                <v-checkbox v-model="item.Optional" @change="updateActivationOptional(item)"></v-checkbox>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon small @click="deleteActivation(item)">
                    <v-icon>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <div class="d-flex justify-end mt-4">
            <v-btn color="primary" @click="addActivation" :disabled="!canAddActivation">
                <v-icon>mdi-plus</v-icon>
                Add Activation
            </v-btn>
        </div>
    </v-container>
</template>


<script>
import ClientAPIMixin from "@/api/ClientAPIMixin";
import ContractSelect from "@/components/Inputs/EntityDropdowns/ContractSelect2.vue";
import CustomerGroupSelect from "@/components/Inputs/EntityDropdowns/CustomerGroupSelect2.vue";
import CustomerSelect from "@/components/Inputs/EntityDropdowns/CustomerSelect2.vue";
import CheckListConstants from "@/helpers/Constants/CheckListConstants";
import MachineModelSelect from "@/components/Inputs/EntityDropdowns/MachineModelSelect2.vue";
import MachineTypeSelect from "@/components/Inputs/EntityDropdowns/MachineTypeSelect2.vue";
import MachineSelect from "@/components/Inputs/EntityDropdowns/MachineSelect2.vue";

// Debounce utility function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        return new Promise((resolve, reject) => {
            const later = () => {
                clearTimeout(timeout);
                func.apply(this, args)
                    .then(resolve)
                    .catch(reject);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        });
    };
}

export default {
    name: "ViewCheckListActivations",
    components: {
        CustomerSelect,
        CustomerGroupSelect,
        ContractSelect,
        MachineModelSelect,
        MachineTypeSelect,
        MachineSelect,
    },
    mixins: [ClientAPIMixin],

    data() {
        return {
            // Debounced update methods
            debouncedUpdateActivation: null,
            debouncedUpdateActivationOptional: null,

            loading: true,
            machineModels: [],
            machineTypes: [],

            template: {
                CheckListActivations: [],
            },

            activationTypeList: CheckListConstants.activationTypeList,
            activationTypeMap: CheckListConstants.activationTypeMap,
            activationTypes: CheckListConstants.activationTypes,

            activationHeaders: [
                { text: "Type", value: "ActivationType" },
                { text: "Key", value: "ActivationKey" },
                //{ text: "Optional", value: "Optional" },
                { text: "Actions", value: "actions", sortable: false }
            ],

        };
    },

    mounted() {
        // Initialize debounced methods
        this.debouncedUpdateActivation = debounce(this.updateCheckListActivation, 2000);
        this.debouncedUpdateActivationOptional = debounce(this.updateCheckListActivation, 2000);
        this.debouncedUpdateCheckListField = debounce(this.updateCheckListField, 1000);

        this.loading = true;
        this.getCheckListTemplate(this.$route.params.templateId)
            .then((templateResponse) => {
                // Ensure templateResponse is not null
                if (!templateResponse) {
                    console.error('Template response is null');
                    this.template = {
                        CheckListActivations: [],
                    };
                    return;
                }

                // Initialize template with reactive properties
                this.template = {
                    ...templateResponse,
                    CheckListActivations: (templateResponse.CheckListActivations || []).map(activation =>
                        this.convertActivationKey(activation)
                    ),
                };

                // Ensure DOM is updated after data is set
                this.$nextTick(() => {
                    console.log('After nextTick - TemplateType:', this.template.TemplateType);
                });
            })
            .catch((error) => {
                console.error('Error loading template:', error);
            })
            .finally(() => {
                this.loading = false;
            });
    },

    methods: {
        // Helper method to convert ActivationKey to appropriate type
        convertActivationKey(activation) {
            let key = activation.ActivationKey;
            // For types that expect integer values, convert string to number
            if (this.activationTypeMap[activation.ActivationType]?.keyConversionType === Number
                && key !== null && key !== undefined && key !== '') {

                key = parseInt(key, 10);
                // If conversion fails, keep original value
                if (isNaN(key)) {
                    key = activation.ActivationKey;
                }
            }

            return {
                ...activation,
                ActivationKey: key
            };
        },

        addActivation() {
            // Get the first available activation type
            const usedTypes = (this.template.CheckListActivations || []).map(a => a.ActivationType);
            const availableType = this.activationTypeList.find(type => !usedTypes.includes(type.value));

            if (!availableType) {
                return; // No available types
            }

            const newActivation = {
                CheckListTemplateId: this.template.Id,
                ActivationType: availableType.value,
                ActivationKey: availableType.value === 1 ? true : (availableType.value === 4 ? true : null), // Set defaults for special types
                Optional: false
            };
            this.addCheckListActivation(this.template.Id, newActivation)
                .then((response) => {
                    if (!this.template.CheckListActivations) {
                        this.template.CheckListActivations = [];
                    }

                    const convertedResponse = this.convertActivationKey(response);
                    this.template.CheckListActivations.push(convertedResponse);
                });
        },

        updateActivation(activation) {
            // For "Always" activation type (1), always update since it doesn't need ActivationKey
            // For "Has Any Contract" (4), always update since it uses a boolean switch
            // For other types, only update if ActivationKey has a value
            if (activation.ActivationType === 1 || activation.ActivationType === 4 ||
                (activation.ActivationKey !== null && activation.ActivationKey !== undefined && activation.ActivationKey !== '')) {
                this.debouncedUpdateActivation(activation);
            }
        },

        updateActivationOptional(activation) {
            // Always update for Optional field changes
            this.debouncedUpdateActivationOptional(activation);
        },

        onActivationTypeChange(activation) {
            // Reset activation key when type changes
            activation.ActivationKey = null;
            // For "Always" activation type, set a default value to indicate it's active
            if (activation.ActivationType === 1) {
                activation.ActivationKey = true; // Indicates "always active"
            }
            // For "Has Any Contract" activation type, set default to true
            if (activation.ActivationType === 4) {
                activation.ActivationKey = true; // Default to "has any contract"
            }
            this.updateActivation(activation);
        },

        availableActivationTypes(currentItem) {
            // Get all currently used activation types except the current item
            const usedTypes = (this.template.CheckListActivations || [])
                .filter(a => a.Id !== currentItem.Id) // Exclude current item
                .map(a => a.ActivationType);

            // Return only activation types that are not used
            return this.activationTypeList.filter(type => !usedTypes.includes(type.value));
        },

        deleteActivation(activation) {
            this.deleteCheckListActivation(activation.Id)
                .then(() => {
                    const index = this.template.CheckListActivations.findIndex(a => a.Id === activation.Id);
                    if (index > -1) {
                        this.template.CheckListActivations.splice(index, 1);
                    }
                });
        },
    },

    computed: {
        contractTypeList() {
            return this.$store.getters["LookUpStore/ContractTypeList"] || [];
        },

        jobPriorityList() {
            return this.$store.getters["LookUpStore/JobPriorityList"] || [];
        },

        jobTypeList() {
            return this.$store.getters["LookUpStore/JobTypeList"] || [];
        },

        canAddActivation() {
            // Check if there are any activation types left to add
            const usedTypes = (this.template.CheckListActivations || []).map(a => a.ActivationType);
            return usedTypes.length < this.activationTypeList.length;
        },

        hasAlwaysActivation() {
            return (this.template.CheckListActivations || []).some(a => a.ActivationType === 1);
        },

        hasAnyContractActivation() {
            return (this.template.CheckListActivations || []).some(a => a.ActivationType === 4);
        }
    },

    watch: {},
};
</script>

<style lang="scss" scoped></style>
